<template>
  <div class="lessons-management-container">
    <!-- Page Header -->
    <div class="page-header">
      <h2 class="page-title">{{ $t('lessonsManagement.title') }}</h2>
    </div>

    <!-- Search Bar -->
    <div class="search-container">
      <i class="fas fa-search search-icon"></i>
      <input 
        type="text" 
        class="search-input" 
        :placeholder="$t('lessonsManagement.searchLessons')" 
        v-model="searchQuery"
        @input="fetchSuggestions"
        @focus="showSuggestions = true"
        @keyup.enter="searchLessons"
      />
      <div class="dropdown-suggestions" v-if="suggestions.length && showSuggestions">
        <ul>
          <li v-for="suggestion in suggestions" :key="suggestion.id" @click="selectSuggestion(suggestion)">
            {{ suggestion.name }} {{ suggestion.last_name }} - {{ suggestion.role }}
          </li>
        </ul>
      </div>
      <button class="btn-search" @click="searchLessons">
        {{ $t('lessonsManagement.search') }}
      </button>
    </div>

    <div class="table-container" v-if="lessons.length">
      <table class="data-table">
        <thead>
          <tr>
            <th @click="setSort('id')" class="sortable">
              {{ $t('lessonsManagement.id') }}
              <i class="sort-icon fas" :class="getSortIconClass('id')"></i>
            </th>
            <th @click="setSort('lesson_date')" class="sortable">
              {{ $t('lessonsManagement.date') }}
              <i class="sort-icon fas" :class="getSortIconClass('lesson_date')"></i>
            </th>
            <th @click="setSort('main_teacher_name')" class="sortable">
              {{ $t('lessonsManagement.mainTeacher') }}
              <i class="sort-icon fas" :class="getSortIconClass('main_teacher_name')"></i>
            </th>
            <th @click="setSort('studygroup_name')" class="sortable">
              {{ $t('lessonsManagement.studygroupName') }}
              <i class="sort-icon fas" :class="getSortIconClass('studygroup_name')"></i>
            </th>
            <th>{{ $t('lessonsManagement.time') }}</th>
            <th>{{ $t('lessonsManagement.studentsList') }}</th>
            <th>{{ $t('lessonsManagement.presence') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="lesson in formattedLessons" :key="lesson.id">
            <td>{{ lesson.id }}</td>
            <td>{{ lesson.lesson_date }}</td>
            <td>{{ lesson.main_teacher_name }}</td>
            <td>{{ lesson.studygroup_name }}</td>
            <td>{{ lesson.time }}</td>
            <td>
              <ul class="students-list">
                <li v-for="student in lesson.students" :key="student" class="student-item">{{ student }}</li>
              </ul>
            </td>
            <td>
              <ul class="presence-list">
                <li v-for="(presence, index) in lesson.presence" :key="index" class="presence-item">
                  <span :class="presence ? 'present' : 'absent'">
                    <i class="fas" :class="presence ? 'fa-check-circle' : 'fa-times-circle'"></i>
                    {{ presence ? $t('lessonsManagement.present') : $t('lessonsManagement.absent') }}
                  </span>
                </li>
              </ul>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <div class="empty-state" v-else>
      <i class="fas fa-book empty-icon"></i>
      <p>{{ $t('lessonsManagement.noLessonsFound') }}</p>
    </div>

    <div class="pagination-controls">
      <div class="page-size-selector">
        <label>{{ $t('lessonsManagement.rowsPerPage') }}:</label>
        <select v-model="pageSize" @change="onPageSizeChange">
          <option v-for="size in [10, 25, 50, 100]" :key="size" :value="size">{{ size }}</option>
        </select>
      </div>
      <div class="pagination-nav">
        <button @click="prevPage" :disabled="currentPage === 1" class="btn btn-secondary">
          {{ $t('lessonsManagement.previous') }}
        </button>
        <span class="pagination-info">{{ $t('lessonsManagement.page') }} {{ currentPage }}</span>
        <button @click="nextPage" :disabled="lessons.length < pageSize" class="btn btn-secondary">
          {{ $t('lessonsManagement.next') }}
        </button>
      </div>
    </div>

    <!-- Toast Notification -->
    <div 
      class="toast-notification" 
      :class="{ show: notificationVisible, success: notificationType === 'success', error: notificationType === 'error' }"
      v-if="notificationVisible"
    >
      <div class="toast-icon">
        <i :class="notificationType === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
      </div>
      <div class="toast-content">
        {{ notificationMessage }}
      </div>
    </div>
  </div>
</template>
  
<script>
import axiosInstance from '../router/axiosSetup';

export default {
  data() {
    return {
      searchQuery: '',
      suggestions: [],
      lessons: [],
      currentPage: 1,
      pageSize: 10, // Default page size now set to 10
      sortKey: 'id',
      sortOrder: 1,
      showSuggestions: false,
      selectedUserId: null,
      notificationVisible: false,
      notificationMessage: '',
      notificationType: 'success',
      isLoading: false
    };
  },
  
  computed: {
    sortedLessons() {
      return this.lessons.slice().sort((a, b) => {
        let result = 0;
        if (this.sortKey === 'balance_after_user') {
          result = a[this.sortKey] - b[this.sortKey];
        } else {
          if (a[this.sortKey] < b[this.sortKey]) {
            result = -1;
          } else if (a[this.sortKey] > b[this.sortKey]) {
            result = 1;
          }
        }
        return result * this.sortOrder;
      });
    },
    
    formattedLessons() {
      return this.sortedLessons.map(lesson => {
        const date = new Date(lesson.lesson_date);
        const formattedDate = date.toLocaleDateString('en-GB', { timeZone: 'Europe/Kiev' }); // Format date to DD/MM/YYYY
        
        // Use the start_time and end_time directly as they are time strings
        const startTime = lesson.start_time ? lesson.start_time.slice(0, 5) : 'N/A'; // Format time as HH:mm
        const endTime = lesson.end_time ? lesson.end_time.slice(0, 5) : 'N/A'; // Format time as HH:mm
        
        return {
          ...lesson,
          lesson_date: formattedDate,
          time: `${startTime} - ${endTime}` // Format time as "Start time - End time"
        };
      });
    }
  },
  
  methods: {
    async fetchSuggestions() {
      if (this.searchQuery.length < 3) {
        this.suggestions = [];
        return;
      }
      
      try {
        const response = await axiosInstance.get(`/admin/search-suggestions?query=${this.searchQuery}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.suggestions = response.data;
        
        // Always show suggestions if we have any
        if (this.suggestions.length > 0) {
          this.showSuggestions = true;
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        this.showNotification('error', this.$t('lessonsManagement.errorFetchingSuggestions'));
      }
    },
    
    async searchLessons() {
      try {
        this.isLoading = true;
        this.showSuggestions = false; // Hide suggestions when searching
        
        // If searchQuery is empty or less than 3 chars, clear the userId filter
        if (!this.searchQuery || this.searchQuery.length < 3) {
          this.selectedUserId = null;
        }
        
        const queryParams = new URLSearchParams();
        if (this.selectedUserId) queryParams.append('userId', this.selectedUserId);
        queryParams.append('limit', this.pageSize);
        queryParams.append('offset', (this.currentPage - 1) * this.pageSize);
        queryParams.append('sortKey', this.sortKey);
        queryParams.append('sortOrder', this.sortOrder === 1 ? 'ASC' : 'DESC');
        
        const response = await axiosInstance.get(`/admin/search-lessons?${queryParams.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        this.lessons = response.data;
        
        if (this.lessons.length === 0 && this.currentPage > 1) {
          // If no results and we're not on the first page, go back one page
          this.currentPage--;
          await this.searchLessons();
        }
      } catch (error) {
        console.error('Error searching lessons:', error);
        this.showNotification('error', this.$t('lessonsManagement.errorSearchingLessons'));
      } finally {
        this.isLoading = false;
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.selectedUserId = null;
      this.currentPage = 1;
      this.searchLessons();
    },
    selectSuggestion(suggestion) {
      this.searchQuery = `${suggestion.name}`;
      this.selectedUserId = suggestion.id;
      this.showSuggestions = false;
      this.currentPage = 1; // Reset to first page when selecting a new suggestion
      this.searchLessons();
    },
    
    setSort(key) {
      if (this.sortKey === key) {
        this.sortOrder *= -1;
      } else {
        this.sortKey = key;
        this.sortOrder = 1;
      }
      this.searchLessons();
    },
    
    getSortIconClass(key) {
      if (this.sortKey !== key) {
        return 'fa-sort';
      }
      return this.sortOrder === 1 ? 'fa-sort-up' : 'fa-sort-down';
    },
    
    nextPage() {
      this.currentPage++;
      this.searchLessons();
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.searchLessons();
      }
    },
    
    onPageSizeChange() {
      this.currentPage = 1; // Reset to first page when changing page size
      this.searchLessons();
    },
    
    showNotification(type, message) {
      this.notificationType = type;
      this.notificationMessage = message;
      this.notificationVisible = true;
      
      // Auto-hide after 3 seconds
      setTimeout(() => {
        this.notificationVisible = false;
      }, 3000);
    },
    
    // Close dropdown suggestions when clicking outside
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showSuggestions = false;
      }
    }
  },
  
  mounted() {
    // Add click outside event listener
    document.addEventListener('click', this.handleClickOutside);
  },
  
  beforeUnmount() {
    // Remove click outside event listener
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>
  
<style scoped>
/* Base layout */
.lessons-management-container {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  color: #333;
}

/* Page header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
}

/* Search container */
.search-container {
  position: relative;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  max-width: 800px;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 16px;
  pointer-events: none;
}

.search-input {
  flex: 1;
  padding: 10px 12px 10px 40px;
  height: 42px;
  border-radius: 21px;
  border: 1px solid #e2e8f0;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  outline: none;
}

.btn-search {
  margin-left: 10px;
  padding: 8px 16px;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 21px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-search:hover {
  background-color: #2563eb;
}

/* Table styling */
.table-container {
  overflow-x: auto;
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
}

.data-table th {
  padding: 16px 12px;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
  text-align: left;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.data-table th.sortable {
  cursor: pointer;
}

.data-table th.sortable:hover {
  background-color: #e9ecef;
}

.sort-icon {
  margin-left: 8px;
  opacity: 0.5;
}

.sort-icon.fa-sort-up,
.sort-icon.fa-sort-down {
  opacity: 1;
}

.data-table tbody tr {
  transition: background-color 0.2s;
  border-bottom: 1px solid #edf2f7;
}

.data-table tbody tr:hover {
  background-color: #f1f5f9;
}

.data-table td {
  padding: 14px 12px;
  vertical-align: middle;
}

/* Students and presence lists */
.students-list,
.presence-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 100px;
  overflow-y: auto;
}

.student-item,
.presence-item {
  padding: 4px 0;
  display: flex;
  align-items: center;
}

.student-item::before {
  content: '•';
  color: #3b82f6;
  margin-right: 6px;
  font-weight: bold;
}

.present, .absent {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  padding: 2px 0;
}

.present {
  color: #10b981;
}

.absent {
  color: #ef4444;
}

.present i, .absent i {
  font-size: 1rem;
}

/* Empty state */
.empty-state {
  padding: 48px 0;
  text-align: center;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 24px;
}

.empty-icon {
  font-size: 3.5rem;
  margin-bottom: 16px;
  color: #d1d5db;
}

.empty-state p {
  font-size: 1.1rem;
}

/* Pagination styles */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0;
  flex-wrap: wrap;
  gap: 16px;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-selector label {
  color: #4b5563;
  font-weight: 500;
  white-space: nowrap;
}

.page-size-selector select {
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background-color: #fff;
  color: #4b5563;
  cursor: pointer;
  min-width: 70px;
}

.page-size-selector select:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
}

.pagination-info {
  color: #4b5563;
  font-size: 0.9rem;
}

.pagination-nav {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn, .btn-secondary {
  background-color: white;
  color: #4b5563;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:hover:not(:disabled),
.btn-secondary:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.btn:disabled,
.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Dropdown suggestions */
.dropdown-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  margin-top: 4px;
}

.dropdown-suggestions ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dropdown-suggestions li {
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.15s;
  border-bottom: 1px solid #f1f5f9;
}

.dropdown-suggestions li:last-child {
  border-bottom: none;
}

.dropdown-suggestions li:hover {
  background-color: #f1f5f9;
}

/* Toast notification */
.toast-notification {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 12px 16px;
  background-color: white;
  border-left: 4px solid #10b981;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  min-width: 300px;
  z-index: 1050;
  transform: translateX(120%);
  transition: transform 0.3s ease;
}

.toast-notification.show {
  transform: translateX(0);
}

.toast-notification.error {
  border-left-color: #ef4444;
}

.toast-notification.error .toast-icon {
  color: #ef4444;
}

.toast-notification.success {
  border-left-color: #10b981;
}

.toast-notification.success .toast-icon {
  color: #10b981;
}

.toast-icon {
  font-size: 1.25rem;
  margin-right: 12px;
}

.toast-content {
  font-size: 0.95rem;
  color: #1f2937;
}

/* Filter section */
.filters-section {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  min-width: 200px;
}

.filter-group label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #4b5563;
  margin-bottom: 6px;
}

.date-range-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.95rem;
}

.date-separator {
  color: #6b7280;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.95rem;
  background-color: white;
}

.filter-select:focus,
.date-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  outline: none;
}

/* Buttons for CRUD operations */
.btn-primary {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 9px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary:hover {
  background-color: #2563eb;
  box-shadow: 0 2px 5px rgba(37, 99, 235, 0.3);
}

/* Action buttons */
.actions-cell {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
}

.btn-icon.edit {
  color: #3b82f6;
  border-color: #3b82f6;
  background-color: white;
}

.btn-icon.edit:hover {
  background-color: #3b82f6;
  color: white;
}

.btn-icon.delete {
  color: #ef4444;
  border-color: #ef4444;
  background-color: white;
}

.btn-icon.delete:hover {
  background-color: #ef4444;
  color: white;
}

.btn-icon.attendance {
  color: #10b981;
  border-color: #10b981;
  background-color: white;
}

.btn-icon.attendance:hover {
  background-color: #10b981;
  color: white;
}

/* Overlay and modal */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(3px);
}

.form-popup {
  background: white;
  padding: 24px;
  border-radius: 12px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pagination-controls {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .search-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    margin-bottom: 8px;
  }
  
  .btn-search {
    margin-left: 0;
    width: 100%;
  }
  
  .filters-section {
    flex-direction: column;
  }
  
  .date-range-inputs {
    flex-direction: column;
    align-items: stretch;
  }
  
  .date-separator {
    display: none;
  }
}
</style>